import { type AnimationGroup, type AssetContainer, Color3, LoadAssetContainerAsync, type Mesh, PBRMaterial, type Scene, Texture, type TransformNode } from "@babylonjs/core";
import { CellMaterial } from "@babylonjs/materials";
import { HERO_LIST, type HeroId } from "@rivalwatch/shared";
import type { ClientHero, RigPart } from "../heroes/types.ts";

interface HeroModelCache {
  base?: AssetContainer;
  skins: Map<string, CellMaterial>;
}

/** CC0 Kenney "Blocky Characters" (node-animated, no skinning) — one shared mesh, one material per hero skin. */
const byScene = new WeakMap<Scene, HeroModelCache>();

async function skinExists(slug: string) {
  try {
    const res = await fetch(`/assets/models/heroes/skins/${slug}.png`, { method: "HEAD" });
    return res.ok;
  } catch {
    return false;
  }
}

export async function loadHeroModels(scene: Scene) {
  const cache: HeroModelCache = { skins: new Map() };
  byScene.set(scene, cache);
  let base: AssetContainer;
  try {
    base = await LoadAssetContainerAsync("/assets/models/heroes/base.glb", scene);
  } catch (e) {
    console.warn("base hero model unavailable, using procedural bodies", e);
    return;
  }
  cache.base = base;
  for (const m of base.meshes) {
    if (m.material instanceof PBRMaterial) m.material = null;
  }
  await Promise.all(
    HERO_LIST.flatMap((h) => [0, ...Array.from({ length: h.forms?.length ?? 0 }, (_, i) => i + 1)].map((form) => ({ h, form }))).map(async ({ h, form }) => {
      const file = form ? `${h.slug}.f${form}` : h.slug;
      if (!(await skinExists(file))) return;
      const cell = new CellMaterial(`hero-${h.slug}-skin`, scene);
      cell.diffuseColor = Color3.White();
      cell.computeHighLevel = true;
      const tex = new Texture(`/assets/models/heroes/skins/${file}.png`, scene, false, false, Texture.NEAREST_SAMPLINGMODE);
      tex.updateSamplingMode(Texture.NEAREST_SAMPLINGMODE);
      cell.diffuseTexture = tex;
      base.materials.push(cell);
      cache.skins.set(`${h.id}:${form}`, cell);
    }),
  );
}

export interface ModelInstance {
  root: TransformNode;
  anims: Map<string, AnimationGroup>;
  /** Model height in its own units (feet at y = 0). */
  height: number;
  /** The model's part meshes (node-animated: clips rotate them; only the inner root node translates). */
  parts: Record<RigPart, Mesh>;
  dispose(): void;
}

const PART_NODES: Record<string, RigPart> = { head: "head", torso: "torso", "arm-left": "armL", "arm-right": "armR", "leg-left": "legL", "leg-right": "legR" };

/** Rescales parts around their joints, keeping the soles on the floor and the chest on the hips. */
function applyShape(p: Record<RigPart, Mesh>, s: NonNullable<ClientHero["shape"]>) {
  const legs = s.legs ?? 1;
  const t = s.torso ?? 1;
  const hip0 = p.legL.position.y;
  const torsoBottom = p.torso.getBoundingInfo().boundingBox.minimum.y;
  const thick = 1 + (legs - 1) * 0.5;
  for (const l of [p.legL, p.legR]) {
    l.position.y = hip0 * legs;
    l.scaling.set(l.scaling.x * thick, l.scaling.y * legs, l.scaling.z * thick);
  }
  p.torso.position.y = hip0 * legs - torsoBottom * t;
  p.torso.scaling.scaleInPlace(t);
  for (const a of [p.armL, p.armR]) a.scaling.scaleInPlace((s.arms ?? 1) / t);
  p.head.scaling.scaleInPlace((s.head ?? 1) / t);
}

export function instantiateHero(scene: Scene, hero: HeroId, shape?: ClientHero["shape"], form = 0): ModelInstance | undefined {
  const cache = byScene.get(scene);
  const c = cache?.base;
  const skin = cache?.skins.get(`${hero}:${form}`) ?? cache?.skins.get(`${hero}:0`);
  if (!c || !skin) return undefined;
  const inst = c.instantiateModelsToScene((n) => `${n}-i`, false, { doNotInstantiate: true });
  const root = inst.rootNodes[0] as TransformNode;
  const parts = {} as Record<RigPart, Mesh>;
  for (const m of root.getChildMeshes(false)) {
    m.material = skin;
    const part = PART_NODES[m.name.replace(/-i$/, "")];
    if (part) parts[part] = m as Mesh;
  }
  if (shape) applyShape(parts, shape);
  const anims = new Map<string, AnimationGroup>();
  for (const g of inst.animationGroups) {
    g.stop();
    anims.set(g.name.replace(/-i$/, ""), g);
  }
  anims.get("idle")?.start(true);
  const { min, max } = root.getHierarchyBoundingVectors(true);
  return {
    root,
    anims,
    height: Math.max(0.1, max.y - min.y),
    parts,
    dispose() {
      for (const g of inst.animationGroups) g.dispose();
      for (const r of inst.rootNodes) r.dispose(false, false);
    },
  };
}
